import React, { useContext, useState, useCallback } from "react";
import { ModalContext } from "context/ModalContext";
import { api_srv } from "services";
import { show_toast } from "utils/helpers";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { CreditCard, Calendar, Briefcase } from "react-feather";

export default function UnlockFeatureForm({ onHide, service }) {
  const [isLoading, setIsLoading] = useState(false);
  const { modalClose } = useContext(ModalContext);

  const validationSchema = Yup.object().shape({
    subscriptionType: Yup.string()
      .oneOf(['monthly', 'yearly'], 'Invalid subscription type')
      .required("Subscription type is required"),
  });

  const handleSubmit = useCallback(
    async (values, { setSubmitting, resetForm }) => {
      setIsLoading(true);
      try {
        console.log("Submitting subscription:", values);
        
        const payload = {
          serviceId: service.id,
          serviceName: service.title,
          subscriptionType: values.subscriptionType,
          price: values.subscriptionType === 'yearly' ? service.price * 10 : service.price, // 10 months discount for yearly
          currency: 'KSH'
        };

        // Simulate API call - replace with actual service
        // const response = await api_srv.create_subscription(payload);
        
        // For demo purposes, we'll simulate a successful response
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        console.log("Subscription created successfully");
        show_toast(`Successfully subscribed to ${service.title}`, "success");
        
        // Close modal and refresh services
        modalClose();
        onHide?.();
        resetForm();
        
        // You might want to trigger a refresh of the services list here
        
      } catch (err) {
        console.error("Error creating subscription:", err.message, err.stack);
        show_toast(`Failed to subscribe to service: ${err.message}`, "error");
      } finally {
        setIsLoading(false);
        setSubmitting(false);
      }
    },
    [service, modalClose, onHide]
  );

  const getDisplayPrice = (subscriptionType) => {
    if (subscriptionType === 'yearly') {
      return service.price * 10; // 10 months price for yearly (2 months free)
    }
    return service.price;
  };

  const getSavingsText = (subscriptionType) => {
    if (subscriptionType === 'yearly') {
      const yearlySavings = service.price * 2;
      return `Save ${yearlySavings} KSH (2 months free)`;
    }
    return '';
  };

  return (
    <div className="container-fluid py-3">
      <div className="card shadow-sm p-4">
        <Formik
          initialValues={{
            subscriptionType: 'monthly',
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, values, handleChange }) => (
            <Form className="needs-validation" noValidate>
              {/* Service Information */}
              <div className="mb-4 pb-4 border-bottom">
                <div className="d-flex align-items-center mb-3">
                  {service?.icon && (
                    <div 
                      style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "12px",
                        background: `${service.color}15`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginRight: "16px"
                      }}
                    >
                      <service.icon 
                        size={24} 
                        style={{ color: service.color }} 
                      />
                    </div>
                  )}
                  <div>
                    <h4 className="mb-1" style={{ fontWeight: '600', color: '#1f2937' }}>
                      {service?.title}
                    </h4>
                    <p className="mb-0" style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                      {service?.description}
                    </p>
                  </div>
                </div>
              </div>

              {/* Pricing Information */}
              <div className="mb-4 pb-4 border-bottom">
                <h5 className="mb-3 d-flex align-items-center">
                  <Briefcase size={20} className="mr-2" style={{ color: '#10b981' }} />
                  Pricing
                </h5>
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="border rounded p-3 bg-light">
                      <div className="text-center">
                        <h6 className="mb-1">Base Price</h6>
                        <div className="h4 mb-0" style={{ color: '#1f2937' }}>
                          {service?.price} <small className="text-muted">KSH/month</small>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="border rounded p-3" style={{ 
                      backgroundColor: values.subscriptionType === 'yearly' ? '#f0f9ff' : '#f8fafc',
                      borderColor: values.subscriptionType === 'yearly' ? '#3b82f6' : '#e5e7eb'
                    }}>
                      <div className="text-center">
                        <h6 className="mb-1">Your Price</h6>
                        <div className="h4 mb-0" style={{ color: '#1f2937' }}>
                          {getDisplayPrice(values.subscriptionType)} <small className="text-muted">KSH</small>
                        </div>
                        {values.subscriptionType === 'yearly' && (
                          <small className="text-success font-weight-bold">
                            {getSavingsText(values.subscriptionType)}
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Subscription Type Selection */}
              <div className="mb-4">
                <h5 className="mb-3 d-flex align-items-center">
                  <Calendar size={20} className="mr-2" style={{ color: '#8b5cf6' }} />
                  Subscription Type
                </h5>
                
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className={`card h-100 cursor-pointer ${values.subscriptionType === 'monthly' ? 'border-primary bg-primary-light' : ''}`}>
                      <div className="card-body text-center">
                        <Field
                          type="radio"
                          name="subscriptionType"
                          value="monthly"
                          className="form-check-input position-absolute"
                          style={{ top: '10px', right: '10px' }}
                          onChange={handleChange}
                        />
                        <Calendar size={32} className="mb-2" style={{ color: '#6366f1' }} />
                        <h6 className="mb-1">Monthly</h6>
                        <p className="mb-0 text-muted small">Pay monthly</p>
                        <div className="h5 mt-2 mb-0">{service?.price} KSH/month</div>
                      </div>
                    </label>
                  </div>
                  
                  <div className="col-md-6">
                    <label className={`card h-100 cursor-pointer position-relative ${values.subscriptionType === 'yearly' ? 'border-primary bg-primary-light' : ''}`}>
                      <div className="card-body text-center">
                        <Field
                          type="radio"
                          name="subscriptionType"
                          value="yearly"
                          className="form-check-input position-absolute"
                          style={{ top: '10px', right: '10px' }}
                          onChange={handleChange}
                        />
                        {/* Popular badge */}
                        <div className="position-absolute" style={{ top: '-8px', left: '50%', transform: 'translateX(-50%)' }}>
                          <span className="badge bg-success">Best Value</span>
                        </div>
                        <Calendar size={32} className="mb-2" style={{ color: '#10b981' }} />
                        <h6 className="mb-1">Yearly</h6>
                        <p className="mb-0 text-muted small">Save 2 months</p>
                        <div className="h5 mt-2 mb-0">
                          {service?.price * 10} KSH/year
                        </div>
                        <small className="text-success">
                          Save {service?.price * 2} KSH
                        </small>
                      </div>
                    </label>
                  </div>
                </div>
                
                <ErrorMessage name="subscriptionType" component="div" className="text-danger small mt-2" />
              </div>

              {/* Summary */}
              <div className="mb-4 p-3 bg-light rounded">
                <h6 className="mb-2">Subscription Summary</h6>
                <div className="d-flex justify-content-between">
                  <span>Service:</span>
                  <span className="font-weight-bold">{service?.title}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Billing:</span>
                  <span className="font-weight-bold text-capitalize">{values.subscriptionType}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Total:</span>
                  <span className="font-weight-bold text-primary">
                    {getDisplayPrice(values.subscriptionType)} KSH
                  </span>
                </div>
                {values.subscriptionType === 'yearly' && (
                  <div className="d-flex justify-content-between text-success">
                    <span>You Save:</span>
                    <span className="font-weight-bold">
                      {service?.price * 2} KSH
                    </span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="d-flex justify-content-end gap-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={modalClose}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || isLoading}
                  className="btn btn-primary d-flex align-items-center"
                >
                  {isLoading || isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard size={16} className="mr-2" />
                      Add to Subscription
                    </>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}